import React from 'react';
import { useScreenClass, SCREEN_CLASSES } from './use-screen-class';
import type { ScreenClass } from './use-screen-class';

const GRID_COLUMNS = 24;

interface BreakpointConfig {
  [key: string]: number;
}

export interface ColProps {
  children?: React.ReactNode;
  className?: string;
  component?: React.ElementType;
  debug?: boolean;
  lg?: number | string;
  md?: number | string;
  offset?: BreakpointConfig;
  pull?: BreakpointConfig;
  push?: BreakpointConfig;
  sm?: number | string;
  style?: React.CSSProperties;
  width?: string | number;
  xl?: number | string;
  xs?: number | string;
  xxl?: number | string;
}

/**
 * Resolve the active value for the current screen class by walking
 * down from the current breakpoint to xs, returning the first defined value.
 */
function resolveBreakpointValue(
  screenClass: ScreenClass,
  values: Partial<Record<ScreenClass, number | string>>
): number | string | undefined {
  const idx = SCREEN_CLASSES.indexOf(screenClass);
  for (let i = idx; i >= 0; i--) {
    const val = values[SCREEN_CLASSES[i]];
    if (val !== undefined) return val;
  }
  return undefined;
}

function resolveBreakpointOffset(
  screenClass: ScreenClass,
  config?: BreakpointConfig
): number {
  if (!config) return 0;
  const idx = SCREEN_CLASSES.indexOf(screenClass);
  for (let i = idx; i >= 0; i--) {
    const val = config[SCREEN_CLASSES[i]];
    if (val !== undefined) return val;
  }
  return 0;
}

const Col: React.FC<ColProps> = ({
  children,
  className,
  component: Component = 'div',
  debug,
  lg,
  md,
  offset,
  pull,
  push,
  sm,
  style: userStyle,
  width: widthProp,
  xl,
  xs,
  xxl
}) => {
  const screenClass = useScreenClass();

  const colValue = resolveBreakpointValue(screenClass, {
    xs,
    sm,
    md,
    lg,
    xl,
    xxl
  });
  const offsetValue = resolveBreakpointOffset(screenClass, offset);
  const pushValue = resolveBreakpointOffset(screenClass, push);
  const pullValue = resolveBreakpointOffset(screenClass, pull);

  const colStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    paddingLeft: 'calc(var(--gutter-width, 26px) / 2)',
    paddingRight: 'calc(var(--gutter-width, 26px) / 2)',
    position: 'relative'
  };

  if (widthProp !== undefined) {
    if (typeof widthProp === 'number') {
      const w = `${widthProp}px`;
      colStyle.flex = `0 0 ${w}`;
      colStyle.maxWidth = w;
    } else if (widthProp === 'auto') {
      colStyle.flex = '0 0 auto';
      colStyle.width = 'auto';
    } else {
      const w = widthProp;
      colStyle.flex = `0 0 ${w}`;
      colStyle.maxWidth = w;
    }
  } else if (colValue === 'content') {
    colStyle.flex = '0 0 auto';
    colStyle.width = 'auto';
  } else if (typeof colValue === 'number') {
    const percentage = `${(colValue / GRID_COLUMNS) * 100}%`;
    colStyle.flex = `0 0 ${percentage}`;
    colStyle.maxWidth = percentage;
  } else {
    // No explicit size: grow to fill available space equally
    colStyle.flexGrow = 1;
    colStyle.flexBasis = 0;
    colStyle.maxWidth = '100%';
  }

  if (offsetValue > 0) {
    colStyle.marginLeft = `${(offsetValue / GRID_COLUMNS) * 100}%`;
  }

  if (pushValue > 0) {
    colStyle.left = `${(pushValue / GRID_COLUMNS) * 100}%`;
  }

  if (pullValue > 0) {
    colStyle.right = `${(pullValue / GRID_COLUMNS) * 100}%`;
  }

  if (debug) {
    colStyle.border = '1px solid red';
  }

  // Merge user styles after computed styles so they can override
  const mergedStyle = { ...colStyle, ...userStyle };

  return (
    <Component className={className} style={mergedStyle}>
      {children}
    </Component>
  );
};

export default Col;
